import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useCaseStudies } from "@/hooks/useCaseStudies";
import { useInsights } from "@/hooks/useInsights";
import HeroThesis from "./HeroThesis";

const Hero = () => {
  const { data: caseStudies } = useCaseStudies();
  const { data: insights } = useInsights();
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // SPai is an editorial case study without a CMS record. Keep hidden projects out of this rail.
  const work = [
    { title: "SPai", slug: "spai", year: "2026" },
    ...(caseStudies || [])
      .filter((study) => ["luno", "rekhta", "leonardo", "future-fabric"].includes(study.slug))
      .map((study) => ({ title: study.title, slug: study.slug, year: study.year || "" })),
  ];
  const writing = (insights || []).slice(0, 5);
  const count = Math.max(work.length, writing.length, 1);

  useEffect(() => {
    if (paused || reduceMotion || count < 2) return;
    const timer = window.setInterval(() => setActive((index) => (index + 1) % count), 6500);
    return () => window.clearInterval(timer);
  }, [count, paused, reduceMotion]);

  const selectedWork = work[active % work.length];
  const selectedEssay = writing.length ? writing[active % writing.length] : null;

  return (
    <section className="pt-[57px]">
      <h1 className="sr-only">
        Arjan Sethi: product, design and AI transformation leader building
        operating systems for how organizations decide, create and execute.
      </h1>
      <div
        className="grid grid-cols-1 md:grid-cols-12 border-b border-border"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
        }}
      >
        <div className="md:col-span-4 px-6 md:px-8 py-5 md:py-6 border-b md:border-b-0 md:border-r border-border flex items-center gap-3 min-h-[112px]">
          <div className="w-9 h-9 shrink-0 rounded-full border border-border flex items-center justify-center">
            <span className="font-display text-base text-foreground leading-none">A.</span>
          </div>
          <div>
            <p className="font-display text-xl uppercase text-foreground leading-none">ARJAN SETHI</p>
            <p className="font-mono text-[9px] uppercase text-muted-foreground mt-2">Product / Design / Systems</p>
          </div>
        </div>

        <Link
          to={`/work/${selectedWork.slug}`}
          className="group md:col-span-4 px-6 md:px-8 py-5 md:py-6 border-b md:border-b-0 md:border-r border-border min-h-[112px] flex flex-col justify-between gap-3 hover:bg-muted/40 focus-visible:outline-none focus-visible:bg-muted/40 transition-colors"
        >
          <span className="font-mono text-[10px] uppercase text-muted-foreground flex items-center justify-between gap-3">
            <span>01 / Recent work</span><span>{selectedWork.year}</span>
          </span>
          <span className="flex items-end justify-between gap-3 min-w-0">
            <span className="font-display text-2xl uppercase text-foreground leading-none truncate">{selectedWork.title}</span>
            <ArrowUpRight aria-hidden="true" className="w-4 h-4 shrink-0 text-accent group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>

        <div className="md:col-span-4 px-6 md:px-8 py-5 md:py-6 min-h-[112px] flex flex-col justify-between gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">02 / Recent thinking</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-6 w-6 rounded-none text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Previous selection" onClick={() => setActive((index) => (index - 1 + count) % count)}><ArrowLeft aria-hidden="true" /></Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 rounded-none text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Next selection" onClick={() => setActive((index) => (index + 1) % count)}><ArrowRight aria-hidden="true" /></Button>
            </div>
          </div>
          <Link to={selectedEssay ? `/thinking/${selectedEssay.slug}` : "/thinking"} className="group flex items-end justify-between gap-3 min-w-0 focus-visible:outline-accent">
            <span className="font-display text-xl uppercase text-foreground leading-none line-clamp-2 group-hover:text-accent transition-colors">
              {selectedEssay?.title || "Explore thinking"}
            </span>
            <ArrowUpRight aria-hidden="true" className="w-4 h-4 shrink-0 text-accent group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Operating thesis */}
      <HeroThesis />
    </section>
  );
};

export default Hero;
